import moment from 'moment'
import { StatsDateBucketInterval } from '@/generated/enums'
import { DateOnlyRange } from '@/components/stats/date_range'

/**
 * parses BE returned bucket labels into format for graph X-axis
 * @param dates strings in ISO format (with or without time)
 * @param bucketInterval what intervals these dates are bucketed by
 */
export const processDateLabels = (
  dates: string[],
  bucketInterval: StatsDateBucketInterval,
): string[] => {
  const datesMomentUTC = dates.map((date) => moment.utc(date, true))
  switch (bucketInterval) {
    case StatsDateBucketInterval.HOURLY: {
      let currentDate = null
      return datesMomentUTC.map((d) => {
        if (!currentDate || !d.isSame(currentDate, 'day')) {
          currentDate = d
          return d.format('MM/DD') // date change; other labels will give context
        }
        return d.format('HH:00')
      })
    }
    case StatsDateBucketInterval.DAILY:
      return datesMomentUTC.map((d) => d.format('MMM DD'))
    case StatsDateBucketInterval.WEEKLY:
      return datesMomentUTC.map((d) => {
        const bucketStart = d.clone().startOf('isoWeek')
        const bucketEnd = bucketStart.clone().add(1, 'week').subtract(1, 'day')
        if (bucketStart.month() === bucketEnd.month()) {
          return `${bucketStart.format('MMM DD')}-${bucketEnd.format('DD')}`
        }
        return `${bucketStart.format('MMM DD')} - ${bucketEnd.format('MMM DD')}`
      })
    case StatsDateBucketInterval.MONTHLY:
      return datesMomentUTC.map((d) => d.format('MMM YYYY'))
    default:
      throw new Error('Invalid bucket interval: ' + bucketInterval)
  }
}

/**
 * parses single date into format for graph tooltip, based on bucket interval
 * ex. '2024-06-17', WEEKLY -> 'Week of June 17, 2024'
 * TODO: clarity on stats UTC timezone
 * @param date string in ISO format (with or without time)
 * @param bucketInterval what intervals these dates are bucketed by
 */
export const processSingleDateLabelFull = (
  date: string | Date,
  bucketInterval: StatsDateBucketInterval,
): string => {
  const dateMomentUTC = moment.utc(date, true)
  if (!dateMomentUTC.isValid()) {
    return `${date}`
  }
  switch (bucketInterval) {
    case StatsDateBucketInterval.HOURLY:
      return dateMomentUTC.format('MMMM D, YYYY HH:mm [UTC]')
    case StatsDateBucketInterval.DAILY:
      return dateMomentUTC.format('ddd, MMMM D, YYYY')
    case StatsDateBucketInterval.WEEKLY: {
      const bucketStart = dateMomentUTC.clone().startOf('isoWeek')
      return bucketStart.format('[Week of] MMMM D, YYYY')
    }
    case StatsDateBucketInterval.MONTHLY:
      return dateMomentUTC.format('MMMM YYYY')
    default:
      throw new Error('Invalid bucket interval: ' + bucketInterval)
  }
}

type InputData = {
  time_bucket: string
  num_alerts?: number // need to join num_alerts and metric into a single "metric" field - requires refactoring
  metric?: number
  key?: string
  [key: string]: any // Allow for any additional fields
}

type ChartDataPoint = {
  date: Date
  values: { [key: string]: number }
  alert_ids?: string[]
}

const ALL_PRODUCTS = 'All Products'

export function aggregateAndGroupTimeSeriesData(
  inputData: InputData[] | undefined | null,
  dateRange: DateOnlyRange,
  groupByField = 'brand',
  topCount = 5, // max number of groups supported by widget colors,
): {
  chartData: ChartDataPoint[]
  legendKeys: string[]
} {
  if (!inputData || !Array.isArray(inputData) || inputData.length === 0) {
    return { chartData: [], legendKeys: [] }
  }

  const groupedData: { [key: string]: { [key: string]: number } } = {}
  const totalsByGroup: { [key: string]: number } = {}
  const alertIdsByDateAndGroup: { [key: string]: { [key: string]: string[] } } = {}

  inputData.forEach((item) => {
    if (!item || typeof item !== 'object') {
      return
    }

    const { time_bucket, num_alerts, alert_ids } = item
    const groupValue = item[groupByField]

    if (!time_bucket || !groupValue || typeof num_alerts !== 'number') {
      return
    }

    const date = moment(time_bucket).startOf('day').format('YYYY-MM-DD')
    if (!groupedData[date]) {
      groupedData[date] = {}
    }
    if (!alertIdsByDateAndGroup[date]) {
      alertIdsByDateAndGroup[date] = {}
    }
    if (!groupedData[date][groupValue]) {
      groupedData[date][groupValue] = 0
    }
    if (!alertIdsByDateAndGroup[date][groupValue]) {
      alertIdsByDateAndGroup[date][groupValue] = []
    }

    // Add num_alerts to the count
    groupedData[date][groupValue] += num_alerts
    totalsByGroup[groupValue] = (totalsByGroup[groupValue] || 0) + num_alerts

    // Add alert_ids to the collection if they exist
    if (alert_ids) {
      alertIdsByDateAndGroup[date][groupValue] = [
        ...alertIdsByDateAndGroup[date][groupValue],
        ...alert_ids,
      ]
    }
  })

  // Get top groups
  const topGroups = Object.entries(totalsByGroup)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topCount)
    .map(([group]) => group)

  const endDate = moment(dateRange.endDate).startOf('day')
  const startDate = moment(dateRange.startDate).startOf('day')
  const dates = []
  for (let m = moment(startDate); m.diff(endDate, 'days') <= 0; m.add(1, 'days')) {
    dates.push(m.format('YYYY-MM-DD'))
  }

  const chartData: ChartDataPoint[] = dates.map((date) => {
    const dateData = groupedData[date] || {}
    const alertIdsData = alertIdsByDateAndGroup[date] || {}
    const values: { [key: string]: number } = {}
    let othersCount = 0
    let alert_ids: string[] = []

    Object.entries(dateData).forEach(([group, count]) => {
      if (topGroups.includes(group)) {
        values[group] = count
        if (alertIdsData[group]) {
          alert_ids = [...alert_ids, ...alertIdsData[group]]
        }
      } else {
        othersCount += count
        if (alertIdsData[group]) {
          alert_ids = [...alert_ids, ...alertIdsData[group]]
        }
      }
    })

    if (othersCount > 0) {
      values['Others'] = othersCount
    }

    return {
      date: moment(date).toDate(),
      values,
      alert_ids,
    }
  })

  const legendKeys = [...topGroups]
  if (chartData.some((data) => 'Others' in data.values)) {
    legendKeys.push('Others')
  }

  return {
    chartData,
    legendKeys,
  }
}

export function processLineAlertGraphData(inputData: InputData[] | undefined | null): {
  chartData: ChartDataPoint[]
  legendKeys: string[]
} {
  if (!inputData || !Array.isArray(inputData) || inputData.length === 0) {
    return { chartData: [], legendKeys: [] }
  }

  const allKeys = Array.from(new Set(inputData.map((item) => item.key)))
  const dataByDate = new Map<string, { [key: string]: number | null }>()

  inputData.forEach((item) => {
    const formattedDate = moment(item.time_bucket).format('YYYY-MM-DD')

    if (dataByDate.has(formattedDate)) {
      const existingValues = dataByDate.get(formattedDate)!
      existingValues[item.key] = item.metric
    } else {
      const values: { [key: string]: number | null } = {}
      allKeys.forEach((key) => {
        values[key] = key === item.key ? item.metric : null
      })
      dataByDate.set(formattedDate, values)
    }
  })

  const chartData: ChartDataPoint[] = Array.from(dataByDate.entries()).map(
    ([date, values]) => ({
      date: new Date(date),
      values,
    }),
  )

  return {
    chartData,
    legendKeys: allKeys,
  }
}
